<template>
	<div
		class="quest-task"
		:class="{ 'quest-task--daily-completed': isDaily && completed }"
	>
		<template v-if="background">
			<RaggedBoxBackground
				v-if="isDaily && completed"
				slug="main-quest-bg-completed"
				:skin="{
					stopColor1: '#1b4d29',
					stopColor2: '#339C51',
				}"
			/>
			<RaggedBoxBackground v-else slug="main-quest-bg" />
		</template>
		<div class="quest-task__content">
			<div class="quest-task__header">
				<p
					class="quest-task-text"
					v-html="replaceGamePassPlaceholders(text)"
				/>
				<Tooltip v-if="hint">
					<template #element>
						<AppIconButton
							:type="IconTypes.INFO"
							:size="12"
							class="quest-task__hint-icon"
						/>
					</template>
					<template #tooltip>
						<span>{{ hint }}</span>
					</template>
				</Tooltip>
			</div>
			<AppProgressBar :progress="progress" />
			<div v-if="completed" class="quest-task--completed">
				<div class="quest-task-text">
					{{ translate("ui.completed") }}
				</div>
				<div class="icon-check" />
			</div>
			<div class="quest-task-text" v-else>
				{{ `${formatNumber(current)}/${formatNumber(target)}` }}
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import RaggedBoxBackground from "@/components/SVG/RaggedBoxBackground.vue";
import Tooltip from "@/components/Tooltip.vue";
import type { FrameName } from "@/game/entities/config";
import { IconTypes } from "@/types/ui";
import { formatNumber } from "@/utils/format";
import { floorNumber } from "@/utils/number";
import { canTranslate, translate } from "@/utils/translate";
import { computed } from "vue";
import AppIconButton from "../AppIconButton.vue";
import AppProgressBar from "../AppProgressBar.vue";

interface Props {
	text: string;
	current: number;
	target: number;
	icon: string | Set<FrameName>;
	building: string | Set<FrameName>;
	buildingLevel?: number;
	town?: string;
	hint?: string;
	questActionRequired?: boolean;
	background?: boolean;
	isDaily?: boolean;
}

const {
	text,
	current,
	target,
	icon,
	building,
	hint,
	buildingLevel = 1,
	town = "default_town",
	questActionRequired,
	background = true,
	isDaily = false,
} = defineProps<Props>();

const completed = computed((): boolean => {
	if (questActionRequired) return false;
	return current >= target;
});

const progress = computed((): number => {
	if (current > target) {
		return 100;
	}
	return floorNumber((current / target) * 100);
});

const replaceGamePassPlaceholders = (text: string): string => {
	let actualIcon = icon;
	if (typeof icon !== "string") {
		actualIcon = "";
	}
	let buildingKey = "";
	if (building instanceof Set) {
		buildingKey = `${Array.from(building)[0]}.name`;
	} else {
		buildingKey = `${building}.name`;
	}

	const replacements = {
		"{number}": `${formatNumber(target)}`,
		"{2to4}": `${formatNumber(target)}`,
		"{level}": `${formatNumber(buildingLevel)}`,
		"{percent}": `${formatNumber(target)}%`,
		"{icon}": `<div class='icon icon-${(actualIcon as string).toLowerCase()}-14'/>`,
		"{building}": canTranslate(buildingKey) ? translate(buildingKey) : "",
		"{town}": `${town}`,
	} as Record<string, string>;

	let str = text;

	str = str.replace(/{\w+}/g, (all: string): string => {
		return replacements[all] || all;
	});

	return str;
};
</script>

<style lang="scss" scoped>
.quest-task {
	position: relative;
	width: 100%;
	@include flex-col-center;
	filter: drop-shadow(0 0 0.125rem rgba(0, 0, 0, 0.4));

	&__content {
		display: flex;
		position: relative;
		flex-direction: column;
		justify-content: center;
		padding: 0.75rem 1.25rem;
		width: 100%;
		height: 100%;

		.quest-task-text {
			color: #372615;
			font-weight: bold;
			font-size: 0.875rem;
		}
	}

	&__header {
		display: flex;
		justify-content: center;
		align-self: flex-start;
		gap: 0.8rem;
	}

	&__hint-icon {
		flex-shrink: 0;
	}

	&--completed {
		@include flex-row-start;
	}
	&--daily-completed {
		.quest-task-text {
			color: #f2dcb5;
		}
	}

	:deep(.ragged-box-bg) {
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 100%;
	}

	:deep(.progress-bar) {
		flex-shrink: 0;
		margin-top: 0.3rem;
		height: 0.313rem;
	}

	:deep(.icon-check) {
		margin-top: 0.25rem;
		margin-left: 0.25rem;
		width: 0.75rem;
		height: 0.75rem;
	}
}
</style>
